'use client';

import * as React from 'react';
import { Input } from './Input';
import { IconButton } from './IconButton';
import { JIcon } from '../../icons';
import { Select } from './Select';
import { cx } from '../utils';
import type { SelectOption } from '../types';
import type { UnsafeProps } from '../escape-hatches';

/* CreatableSelect deliberately does NOT forwardRef. Unlike the
   other interactive form primitives (Input, Textarea, Select,
   Checkbox, SearchInput) which wrap a single focusable DOM node,
   this component swaps between a <Select> and a <Button>-adjacent
   <Input> via a local state machine. A consumer-held ref would
   point at whichever branch happened to be mounted this render —
   either unreliable (disappears across state transitions) or
   misleading (points at the wrong element half the time). If a
   caller needs a handle to the currently-visible control, wrap a
   ref around the child nested in the appropriate branch and
   reach for it conditionally; or use the `value` / `onChange`
   contract the primitive already exposes. The CONTRIBUTING.md
   "forwardRef only on primitives that wrap a focusable DOM node"
   rule explicitly scopes this out. */
export interface CreatableSelectProps extends UnsafeProps {
  value?: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  addLabel?: string;
  newPlaceholder?: string;
  className?: string;
  /** Visual size variant. Routes through ADR-045 `--control-h`. @default 'md' */
  size?: 'sm' | 'md' | 'lg';
  /** When true, applies error border styling to the Select. */
  error?: boolean;
  /** Visible error text shown with the error icon when `error` is true. */
  errorMessage?: string;
  /** When true, disables the Select and hides the "Add item..." sentinel. */
  disabled?: boolean;
  /** Render an inline search row in the Select dropdown. */
  search?: boolean;
  /** Placeholder for the optional Select search row. */
  searchPlaceholder?: string;
  /** Message shown when search filters out every option. */
  emptyMessage?: string;
  /** Accessible label for the inline create input (used in create mode). */
  createInputLabel?: string;
  /** Accessible label forwarded to the underlying Select. */
  'aria-label'?: string;
  /** Accessible label reference forwarded to the underlying Select. */
  'aria-labelledby'?: string;
}

export function CreatableSelect({
  value,
  onChange,
  options,
  placeholder,
  addLabel = 'Add item...',
  newPlaceholder = 'New value\u2026',
  className,
  size = 'md',
  error,
  errorMessage = 'Selection needs attention',
  disabled,
  search,
  searchPlaceholder,
  emptyMessage,
  createInputLabel = 'New option value',
  unsafe_className,
  unsafe_style,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: CreatableSelectProps) {
  const [mode, setMode] = React.useState<'select' | 'create'>('select');
  const [draft, setDraft] = React.useState('');
  const reactSentinelId = React.useId().replace(/[^\w-]/g, '');
  const errorId = React.useId();

  const commit = () => {
    if (disabled) return;
    const v = draft.trim();
    if (!v) {
      setMode('select');
      setDraft('');
      return;
    }
    onChange(v);
    setDraft('');
    setMode('select');
  };

  const cancel = () => {
    setDraft('');
    setMode('select');
  };

  if (mode === 'create') {
    return (
      <div
        className={cx(
          'j-creatable-select-create',
          size === 'sm' && 'j-creatable-select-create-sm',
          size === 'lg' && 'j-creatable-select-create-lg',
          className,
          unsafe_className,
        )}
        style={unsafe_style}
      >
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={newPlaceholder}
          disabled={disabled}
          aria-label={createInputLabel}
          size={size}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- user-initiated mode switch; focus should land on the input
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (!disabled) commit();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
        />
        <IconButton size="sm" ariaLabel="Add" onClick={commit} disabled={disabled || !draft.trim()}>
          <JIcon intent="check" decorative size={16} />
        </IconButton>
        <IconButton size="sm" ariaLabel="Cancel" onClick={cancel}>
          <JIcon intent="close" decorative size={16} />
        </IconButton>
      </div>
    );
  }

  const optList = options.slice();
  if (value && !optList.some((o) => o.value === value)) {
    optList.unshift({ value, label: value });
  }
  /* Unique sentinel per mounted instance: React.useId guarantees it
     can't collide with any user-supplied value. */
  const sentinel = `__jkit_add_new__${reactSentinelId}`;
  return (
    <div
      className={cx(
        'j-creatable-select',
        size === 'sm' && 'j-creatable-select-sm',
        size === 'lg' && 'j-creatable-select-lg',
      )}
    >
      <Select
        value={value ?? ''}
        placeholder={placeholder}
        className={className}
        size={size}
        error={error}
        disabled={disabled}
        search={search}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        // eslint-disable-next-line @atelierpx/jacquard/no-experimental-without-prefix
        unsafe_className={unsafe_className}
        // eslint-disable-next-line @atelierpx/jacquard/no-experimental-without-prefix
        unsafe_style={unsafe_style}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={error ? errorId : undefined}
        onValueChange={(v: string) => {
          if (v === sentinel) {
            setMode('create');
            return;
          }
          onChange(v);
        }}
        options={
          disabled
            ? optList
            : [
                ...optList,
                { value: sentinel, label: addLabel, _dividerBefore: true, _action: true },
              ]
        }
      />
      {error && (
        <div id={errorId} className="j-creatable-select-status" role="alert">
          <JIcon intent="error" size={14} aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
}
